import React from 'react';

import { StandardEventProperties } from '@betterment/js-runtime';
import { Box, Button, ButtonProps, DropdownMenu, Icon, IconName, IconProps, Link, Text } from '@betterment/samba-react';
import { SystemStyleObject } from '@betterment/samba-styled-system/types';

export type Data = { action: string; href: string };
export type Item = {
  title: string;
  trackingEventData: StandardEventProperties;
  disabled?: boolean;
} & (
  | {
      path?: string;
      data?: Data;
    }
  | { onClick: () => void }
);
export type Separator = { separator: boolean };

export type ActionMenuProps = {
  menuItems: Array<Item | Separator>;
  buttonText?: string;
  iconName?: IconName;
  variant?: ButtonProps['variant'];
  IconColor?: IconProps['color'];
  trackingEventData?: StandardEventProperties;
  css?: SystemStyleObject;
};

export const ActionMenuItems: React.FC<React.PropsWithChildren<Pick<ActionMenuProps, 'menuItems'>>> = ({
  menuItems,
}) => (
  <>
    {menuItems.map((item, index) => {
      let key: string | number = index;
      if ('title' in item) key = item.title;
      return (
        <Box as="span" key={key}>
          {'separator' in item && <DropdownMenu.Separator />}
          {'path' in item && (
            <DropdownMenu.Item
              data-action={item.data?.action}
              data-href={item.data?.href}
              data-track-event="ElementClicked"
              data-track-module={item.trackingEventData.module}
              data-track-name={item.trackingEventData.name}
              disabled={item.disabled}
              tabIndex={item.disabled ? -1 : 0}
            >
              {item.disabled ? <Text variant="text100">{item.title}</Text> : <Link href={item.path}>{item.title}</Link>}
            </DropdownMenu.Item>
          )}
          {'onClick' in item && (
            <DropdownMenu.Item
              data-track-event="ElementClicked"
              data-track-module={item.trackingEventData.module}
              data-track-name={item.trackingEventData.name}
              disabled={item.disabled}
              tabIndex={item.disabled ? -1 : 0}
              onSelect={item.onClick}
            >
              <Text variant="text100">{item.title}</Text>
            </DropdownMenu.Item>
          )}
        </Box>
      );
    })}
  </>
);

const ActionMenu: React.FC<React.PropsWithChildren<ActionMenuProps>> = ({
  buttonText,
  variant = 'solidSecondary',
  menuItems,
  iconName = 'CaretDown',
  IconColor = variant === 'solidPrimary' ? 'icon.inverse' : 'icon',
  trackingEventData,
  css,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        asChild={true}
        data-track-event="ElementClicked"
        data-track-module={trackingEventData?.module}
        data-track-name={trackingEventData?.name}
      >
        <Button
          variant={variant}
          css={{
            minHeight: 0,
            ...css,
          }}
        >
          <Box
            as="span"
            css={{
              alignContent: 'center',
              display: 'inline-flex',
              mx: buttonText ? 'space.800' : 'space.0',
              ...css,
            }}
          >
            {buttonText}
            <Icon name={iconName} size="small" color={IconColor} css={{ p: 0 }} />
          </Box>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="center" side="bottom">
        <ActionMenuItems menuItems={menuItems} />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default ActionMenu;
